import { Sun, CheckSquare, Settings, LogOut } from 'lucide-react';
import { NavCountBadge, ReviewIcon } from '@/components/ui';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { useOwnAvatar } from '@/hooks';

export interface SidebarProps {
  activeItem?: string;
  userName?: string;
  userEmail?: string;
  reviewCount?: number;
  /** Items on Today. See MobileDrawer.todayCount. */
  todayCount?: number;
  onNavigate?: (path: string) => void;
  onSignOut?: () => void;
  /**
   * When false, hide the Review tab from the nav. The review pipeline
   * is owner-only — invited members can't approve emails into someone
   * else's schedule, so the tab has nothing actionable for them.
   */
  showReviewTab?: boolean;
}

const ALL_NAV_ITEMS = [
  { path: '/today', label: 'Today', icon: Sun },
  { path: '/review', label: 'Review', icon: ReviewIcon },
  { path: '/schedule', label: 'Schedule', icon: CheckSquare },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export function Sidebar({
  activeItem = '/today',
  userName,
  userEmail,
  reviewCount,
  todayCount,
  onNavigate,
  onSignOut,
  showReviewTab = true,
}: SidebarProps) {
  const navItems = showReviewTab
    ? ALL_NAV_ITEMS
    : ALL_NAV_ITEMS.filter((item) => item.path !== '/review');
  const ownAvatar = useOwnAvatar();
  const initials = userName
    ? userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : userEmail
      ? userEmail[0].toUpperCase()
      : 'U';

  return (
    <aside className="w-60 shrink-0 self-stretch bg-background border-r border-border flex flex-col">
      <div className="px-5 py-6">
        <span className="text-3xl font-black tracking-tight text-text-brand">
          Yaruta
        </span>
      </div>
      <nav className="flex-1 px-3">
        {navItems.map(({ path, label, icon: Icon }) => {
          const isActive = activeItem === path;
          return (
            <button
              key={path}
              onClick={() => onNavigate?.(path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-normal mb-1 transition-colors cursor-pointer ${
                isActive
                  ? 'bg-surface-muted text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon size={20} />
              {label}
              {path === '/review' && (
                <NavCountBadge
                  count={reviewCount}
                  tone="error"
                />
              )}
              {path === '/today' && (
                <NavCountBadge
                  count={todayCount}
                  tone="neutral"
                />
              )}
            </button>
          );
        })}
      </nav>
      <div className="px-4 py-4">
        <div className="flex items-center gap-3 mb-3">
          <ProfileAvatar
            imagePath={ownAvatar.imagePath}
            directUrl={ownAvatar.directUrl}
            emoji={ownAvatar.emoji}
            color={ownAvatar.color}
            initials={initials}
            size="sm"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {userName || 'User'}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {userEmail || ''}
            </p>
          </div>
        </div>
        <button
          onClick={onSignOut}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted rounded-md transition-colors cursor-pointer"
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>
    </aside>
  );
}
